/**
 * Turns a raw site submission into a `CanonicalLead`.
 *
 * Sites were built at different times by different hands, so their PocketBase
 * collections use different field names for the same thing. Rather than force a
 * migration across every site, this maps the common aliases automatically and
 * lets a site override any field explicitly in `src/sites.ts`.
 *
 * Pure and total: it never throws on weird input. A submission we can't read is
 * still a lead — it gets stored with whatever we could extract, and the quality
 * checks decide whether it is worth forwarding.
 */
import type { CanonicalLead, LeadDetail, LeadFields, SiteDefinition } from "../types.ts";
import {
  DETAIL_EXCLUDED,
  DETAIL_LABELS,
  DETAIL_ORDER,
  humanizeLabel,
  humanizeValue,
} from "./vocabulary.ts";

export type RawRecord = Record<string, unknown>;

/** PocketBase bookkeeping fields — never lead data. */
const SYSTEM_FIELDS = new Set([
  "id",
  "created",
  "updated",
  "collectionid",
  "collectionname",
  "expand",
]);

/**
 * Alias table. Keys are compared after lowercasing and stripping every
 * non-alphanumeric character, so "Full Name", "full_name" and "fullName" all
 * collapse to "fullname".
 */
const FIELD_ALIASES: Record<keyof LeadFields, string[]> = {
  name: ["name", "fullname", "yourname", "contactname", "customername", "leadname", "person"],
  email: ["email", "emailaddress", "youremail", "contactemail", "mail", "emailaddr"],
  phone: ["phone", "phonenumber", "telephone", "tel", "mobile", "cell", "contactphone", "yourphone"],
  company: ["company", "companyname", "business", "businessname", "organization", "organisation", "org"],
  city: ["city", "town", "locality"],
  state: ["state", "province", "region", "stateprovince"],
  postalCode: ["zip", "zipcode", "postalcode", "postcode", "postal"],
  message: [
    "message",
    "comments",
    "comment",
    "details",
    "notes",
    "inquiry",
    "enquiry",
    "question",
    "description",
    "howcanwehelp",
    "additionalinfo",
    "body",
  ],
  interest: [
    "interest",
    "interestedin",
    "machinetype",
    "machine",
    "servicetype",
    "service",
    "product",
    "producttype",
    "subject",
    "lookingfor",
    "type",
    "category",
  ],
  sourceUrl: [
    "sourceurl",
    "sourcepage",
    "pageurl",
    "url",
    "page",
    "landingpage",
    "submittedfrom",
    "formurl",
  ],
  referrer: ["referrer", "referer", "ref", "referredby", "referralsource"],
  ip: ["ip", "ipaddress", "clientip", "remoteip", "userip"],
  userAgent: ["useragent", "ua", "browser"],
  details: [], // built by extractDetails, not by alias lookup
  utm: [], // handled separately
};

/** Split-name fallbacks, used only when no single name field is present. */
const FIRST_NAME_ALIASES = ["firstname", "fname", "givenname", "first"];
const LAST_NAME_ALIASES = ["lastname", "lname", "surname", "familyname", "last"];

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid"];

/** Lowercase + strip non-alphanumerics, so field-name styles stop mattering. */
export function canonicalKey(key: string): string {
  return key.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/** Coerce any stored value into a trimmed display string. */
export function toText(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return value.map(toText).filter(Boolean).join(", ");
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return "";
    }
  }
  return "";
}

/** Collapse runs of whitespace so emails and reports render predictably. */
export function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

/**
 * Find the first non-empty value among candidate field names.
 * Candidates are matched canonically, so callers can pass any casing/style.
 */
export function pickField(record: RawRecord, candidates: readonly string[]): string {
  const lookup = new Map<string, unknown>();
  for (const [key, value] of Object.entries(record)) {
    lookup.set(canonicalKey(key), value);
  }

  for (const candidate of candidates) {
    const value = lookup.get(canonicalKey(candidate));
    const text = toText(value);
    if (text.length > 0) return text;
  }
  return "";
}

function extractUtm(record: RawRecord): Record<string, string> {
  const utm: Record<string, string> = {};

  // Direct utm_* columns.
  for (const [key, value] of Object.entries(record)) {
    const canonical = canonicalKey(key);
    for (const utmKey of UTM_KEYS) {
      if (canonical === canonicalKey(utmKey)) {
        const text = toText(value);
        if (text) utm[utmKey] = text;
      }
    }
  }

  // A nested `utm` object or JSON string, which some sites use instead.
  const nested = record["utm"] ?? record["utm_params"] ?? record["utmParams"];
  const nestedObject = asObject(nested);
  if (nestedObject) {
    for (const [key, value] of Object.entries(nestedObject)) {
      const text = toText(value);
      if (text) utm[key.toLowerCase()] = text;
    }
  }

  // Parameters left on the submitted page URL.
  const url = pickField(record, FIELD_ALIASES.sourceUrl);
  if (url) {
    try {
      const parsed = new URL(url);
      for (const utmKey of UTM_KEYS) {
        const value = parsed.searchParams.get(utmKey);
        if (value && !utm[utmKey]) utm[utmKey] = value;
      }
    } catch {
      // Not a parseable URL; nothing to harvest.
    }
  }

  return utm;
}

function asObject(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === "object" && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  if (typeof value === "string" && value.trim().startsWith("{")) {
    try {
      const parsed: unknown = JSON.parse(value);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // Not JSON.
    }
  }
  return null;
}

/** Candidate field names for one canonical field, honouring site overrides. */
function candidatesFor(site: SiteDefinition, field: keyof LeadFields): string[] {
  const override = site.map?.[field];
  if (override === undefined) return FIELD_ALIASES[field];
  const list = Array.isArray(override) ? override : [override];
  // An override wins, but fall back to the defaults if it finds nothing.
  return [...list, ...FIELD_ALIASES[field]];
}

function extractName(site: SiteDefinition, record: RawRecord): string {
  const direct = pickField(record, candidatesFor(site, "name"));
  if (direct) return collapseWhitespace(direct);

  const first = pickField(record, FIRST_NAME_ALIASES);
  const last = pickField(record, LAST_NAME_ALIASES);
  return collapseWhitespace([first, last].filter(Boolean).join(" "));
}

/** Lowercased and trimmed; used for both storage and duplicate detection. */
export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase();
}

/** Digits only, so "(555) 123-4567" and "555-123-4567" compare equal. */
export function normalizePhone(value: string): string {
  const digits = value.replace(/\D/g, "");
  // Drop a leading US country code so +1 555... matches 555...
  if (digits.length === 11 && digits.startsWith("1")) return digits.slice(1);
  return digits;
}

/** True when the submission tripped one of the site's honeypot fields. */
export function trippedHoneypot(site: SiteDefinition, record: RawRecord): boolean {
  const fields = site.honeypotFields ?? [];
  if (fields.length === 0) return false;
  return fields.some((field) => pickField(record, [field]).length > 0);
}

export function normalizeLead(site: SiteDefinition, record: RawRecord): CanonicalLead {
  const recordId = toText(record["id"]);
  const submittedAt = toText(record["created"]) || new Date().toISOString();

  const fields: LeadFields = {
    name: extractName(site, record),
    email: normalizeEmail(pickField(record, candidatesFor(site, "email"))),
    phone: collapseWhitespace(pickField(record, candidatesFor(site, "phone"))),
    company: collapseWhitespace(pickField(record, candidatesFor(site, "company"))),
    city: collapseWhitespace(pickField(record, candidatesFor(site, "city"))),
    state: collapseWhitespace(pickField(record, candidatesFor(site, "state"))),
    postalCode: collapseWhitespace(pickField(record, candidatesFor(site, "postalCode"))),
    // Message keeps its line breaks — it's prose the partner will read.
    message: pickField(record, candidatesFor(site, "message")).trim(),
    interest: humanizeValue(collapseWhitespace(pickField(record, candidatesFor(site, "interest")))),
    details: extractDetails(site, record),
    sourceUrl: collapseWhitespace(pickField(record, candidatesFor(site, "sourceUrl"))),
    referrer: collapseWhitespace(pickField(record, candidatesFor(site, "referrer"))),
    utm: extractUtm(record),
    ip: collapseWhitespace(pickField(record, candidatesFor(site, "ip"))),
    userAgent: collapseWhitespace(pickField(record, candidatesFor(site, "userAgent"))),
  };

  return {
    ...fields,
    sourceKey: buildSourceKey(site.collection, recordId),
    sourceCollection: site.collection,
    sourceRecordId: recordId,
    siteKey: site.key,
    submittedAt: toIsoOrNow(submittedAt),
    raw: { ...record },
  };
}

export function buildSourceKey(collection: string, recordId: string): string {
  return `${collection}:${recordId}`;
}

/** PocketBase stores dates as "2026-08-14 12:00:00.000Z"; normalize to real ISO. */
export function toIsoOrNow(value: string): string {
  if (!value) return new Date().toISOString();
  const direct = new Date(value);
  if (!Number.isNaN(direct.getTime())) return direct.toISOString();

  const spaceReplaced = new Date(value.replace(" ", "T"));
  if (!Number.isNaN(spaceReplaced.getTime())) return spaceReplaced.toISOString();

  return new Date().toISOString();
}

/** Canonical keys already consumed by a named lead field, a honeypot, or a UTM. */
function claimedKeys(site: SiteDefinition): Set<string> {
  const claimed = new Set<string>();
  for (const field of Object.keys(FIELD_ALIASES) as (keyof LeadFields)[]) {
    if (field === "details") continue;
    for (const candidate of candidatesFor(site, field)) claimed.add(canonicalKey(candidate));
  }
  for (const alias of [...FIRST_NAME_ALIASES, ...LAST_NAME_ALIASES, ...UTM_KEYS]) {
    claimed.add(canonicalKey(alias));
  }
  for (const field of site.honeypotFields ?? []) claimed.add(canonicalKey(field));
  return claimed;
}

/** Fields the pipeline understood, for surfacing unmapped columns in diagnostics. */
export function unmappedFields(site: SiteDefinition, record: RawRecord): string[] {
  const claimed = claimedKeys(site);

  return Object.keys(record).filter((key) => {
    const canonical = canonicalKey(key);
    if (SYSTEM_FIELDS.has(canonical)) return false;
    if (claimed.has(canonical)) return false;
    return toText(record[key]).length > 0;
  });
}

/**
 * Every qualifying answer the form captured that isn't already a named field.
 *
 * Auto-capturing rather than requiring per-site configuration is deliberate: a
 * site adding a new question must reach the partner by default. The cost of an
 * extra row in an email is trivial; the cost of silently dropping "they want to
 * lease, for a school" is a lost sale.
 */
export function extractDetails(site: SiteDefinition, record: RawRecord): LeadDetail[] {
  const claimed = claimedKeys(site);
  const excluded = new Set([
    ...DETAIL_EXCLUDED,
    ...(site.excludeFields ?? []).map(canonicalKey),
  ]);

  const details: Array<LeadDetail & { rank: number; column: string }> = [];

  for (const [column, raw] of Object.entries(record)) {
    const canonical = canonicalKey(column);
    if (SYSTEM_FIELDS.has(canonical) || claimed.has(canonical) || excluded.has(canonical)) continue;

    const value = humanizeValue(toText(raw));
    if (!value) continue;

    const label =
      site.detailLabels?.[column] ??
      site.detailLabels?.[canonical] ??
      DETAIL_LABELS[canonical] ??
      humanizeLabel(column);

    const rank = DETAIL_ORDER.indexOf(canonical);
    details.push({ label, value, rank: rank === -1 ? Number.MAX_SAFE_INTEGER : rank, column });
  }

  // Most decision-relevant answers first, then a stable alphabetical tail.
  details.sort((a, b) => a.rank - b.rank || a.column.localeCompare(b.column));

  return details.map(({ label, value }) => ({ label, value }));
}
